import { Observable, of, merge, Subject, catchError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IAuthToken } from '@server/auth/interfaces/authtoken.interface';
import { IUserRegisterInput } from '@server/auth/interfaces/user-registration.interface';
export interface ITokenResponse {
  token: string;
}
export type ConfirmMethod = 'url' | 'code';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authTokenSubject: Subject<IAuthToken>;

  get authTokenChange(): Observable<IAuthToken> {
    return merge(this.authToken, this.authTokenSubject.asObservable());
  }

  get isAuthenticated(): boolean {
    // Check whether the token is expired and return
    // true or false
    if (this._jwtHelperService.isTokenExpired()) {
      this.invalidate();
      return false;
    }
    return true;
  }

  private get authToken(): Observable<IAuthToken> {
    const offset = (13 * 24 * 60 * 60) + (23 * 60 * 60); // 13 giorni e 23 ore. Un'ora in meno della validità iniziale del token.
    // se è passata almeno un'ora dall'ultima visita / refresh del token, eseguo un ulteriore refresh
    // https://stackoverflow.com/questions/26739167/jwt-json-web-token-automatic-prolongation-of-expiration
    if (this._jwtHelperService.isTokenExpired(undefined, offset)) {
      return this.refreshToken();
    }
    // return this.refreshToken();
    return of(this._jwtHelperService.decodeToken() as IAuthToken);
  }

  constructor(
    protected readonly router: Router,
    protected readonly http: HttpClient,
    private _jwtHelperService: JwtHelperService,
  ) {

    this.authTokenSubject = new Subject<IAuthToken>();
  }

  validateTurnsiteToken(token : string):Observable<any> {

    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    const body = JSON.stringify({token});

    return this.http.post(this.getEndPointUrl('validate'), {token},
      {
        headers: _headers
      });
  }


  authenticate(email: string, password: string, redirectUrl?: string): Observable<boolean> {
    const endPoint = this.getEndPointUrl('login');
    // occorre inserire Accept header altrimenti sul server il body della richiesta risulta vuoto.
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    const body = JSON.stringify({ email, password });

    return this.http.post(endPoint,
      body,
      {
        headers: _headers
      }).pipe(
        map((data: ITokenResponse) => {
          if (data) {
            this.setauthToken(data.token, redirectUrl);
            return true;
          }
          return false;
        })
      );
  }

  registerUser(userInput: IUserRegisterInput, confirmMethod: ConfirmMethod = 'url'): Observable<boolean> {
    const endPoint = this.getEndPointUrl('register');
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');
    let httpParams = new HttpParams()
    .append('confirm-method', confirmMethod);
    return this.http.post(endPoint,
      JSON.stringify(userInput),
      {
        headers: _headers,
        params: httpParams
      }).pipe(
        map((data: ITokenResponse) => {
          if (data) {
            this.setauthToken(data.token, null);
            return true;
          }

          return false;
        }));
  }

  confirmCode(code: { code: string }): Observable<boolean> {
    const endPoint = this.getEndPointUrl('confirm-code');
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    return this.http.post(endPoint,
      JSON.stringify(code),
      {
        headers: _headers
      }).pipe(
      map((data: ITokenResponse) => {
        if (data) {
          this.setauthToken(data.token, null);
          this.authTokenSubject.next(this._jwtHelperService.decodeToken() as IAuthToken);
          return true;
        }

        return false;
      }));
  }

  resendConfirmCode(id: string): Observable<boolean> {
    const endPoint = this.getEndPointUrl('resend-confirm-code');
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    return this.http.post(endPoint,
      {id: id},
      {
        headers: _headers
      }).pipe(
      map((res: boolean) => res)
    );
  }

  invalidate(): void {
    this.setauthToken(null, this.router.url);
  }

  // protected getUrlSearchParams = (params: Array<any>): URLSearchParams => {
  //   const res = new URLSearchParams();

  //   for (const item of params)
  //     res.append(item.key, item.value);

  //   return res;
  // };

  protected getEndPointUrl = (url: string) => {
    return '/auth/' + url;
  }

  setauthToken(token: string, redirectUrl?: string): void {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
    this.authTokenSubject.next(this._jwtHelperService.decodeToken() as IAuthToken);
    if (redirectUrl) {
      this.router.navigated = false;
      this.router.navigateByUrl(redirectUrl);
    }
  }

  // protected handleError(error: Response): Observable<never> {
  //   // in a real world app, we may send the server to some remote logging infrastructure
  //   // instead of just logging it to the console
  //   return observableThrowError(error || "Server Error");
  // }

  refreshToken(): Observable<IAuthToken> {
    const endPoint = this.getEndPointUrl('refresh-token');
    if (!this._jwtHelperService.isTokenExpired()) {
      return this.http.get(endPoint, { headers: {"authorization": `Bearer ${localStorage.getItem('token') || null}`}})
      .pipe(
        tap((data: ITokenResponse) => this.setauthToken(data.token)),
        map((data) => this._jwtHelperService.decodeToken() as IAuthToken)
      );
    } else {
      return of(null);
    }
  }

  forgotPassword(email:string): Observable<boolean>{
    const endPoint = this.getEndPointUrl('forgot-password');
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    const body = JSON.stringify({ email });

    return this.http.post(endPoint,
      body,
      {
        headers: _headers
      }).pipe(map((success: boolean) => success));
  }

  resetPassword(token: string, password: string): Observable<boolean>{
    const endPoint = this.getEndPointUrl('reset-password');
    const _headers: HttpHeaders = new HttpHeaders().set('Content-Type', 'application/json').set('Accept', 'application/json');

    const body = JSON.stringify({ token, password });

    return this.http.post(endPoint,
      body,
      {
        headers: _headers
      }).pipe(map((success: boolean) => success));

  }
}
